function selectedId(value) {
  return value && typeof value === 'object' ? value.id : value || '';
}

function tick(on) {
  return on ? '☑' : '☐';
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function optionLines(groups, picked) {
  const lines = [];
  for (const group of groups || []) {
    lines.push(group.label.toUpperCase());
    for (const option of group.options || []) {
      const on = picked(option.id);
      const extra = on && option.id === 'other' ? ' ________' : '';
      lines.push(`  ${tick(on)} ${option.label}${extra}`);
    }
    lines.push('');
  }
  return lines;
}

export function nclPlainText(template, fields, context) {
  const providerId = selectedId(fields.provider);
  const actionId = selectedId(fields.action);
  const submissionId = selectedId(fields.submission);
  const concernIds = new Set(fields.concerns?.ids || []);
  const lines = [
    template.displayName,
    '',
    'This form is to be used for raising any concerns with a provider, by generating Feedback, a GP alert, or a Patient Safety Event.',
    '',
    `Email completed form to: ${template.email}`,
    'Send as a Word document or via Accumail / Accurx. Do NOT send the form as a PDF.',
    template.attachmentNote || '',
    '',
    `Date: ${context?.completedOn || ''}`,
    `Patient name: ${context?.patientName || ''}    DOB: ${context?.dateOfBirth || ''}    NHS number: ${context?.nhsNumber || ''}`,
    `GP's name: ${context?.raiserName || ''}    GP practice: ${context?.practiceName || ''}`,
    `GP GMC number: ${context?.gmcNumber || ''}    Practice code: ${context?.odsCode || ''}`,
    `Reporter name (if not GP): ${context?.reporterName || ''}`,
    '',
    'Please complete all sections marked *.',
    'Sections marked "select ONE only" must not be left blank or have more than one option selected.',
    '',
    '1) PROVIDER (please select ONE only)',
    'If your concern relates to more than one provider, please submit a separate form for each provider.',
    '',
    ...optionLines(template.providers, (id) => id === providerId),
  ];
  if (providerId === 'other' && fields.provider?.other) {
    lines.push(`Other (specify): ${fields.provider.other}`, '');
  }
  lines.push(
    '2) SPECIALTY / SERVICE (required)',
    fields.specialty || '',
    '',
    `Specialty / service email (optional): ${fields.specialtyEmail || ''}`,
    `Consultant / lead clinician name (optional): ${fields.consultantName || ''}`,
    '',
    '3) NATURE OF CONCERN (select all that apply)',
    '',
    ...optionLines(template.concerns, (id) => concernIds.has(id)),
  );
  if (concernIds.has('other') && fields.concerns?.other) {
    lines.push(`Other: ${fields.concerns.other}`, '');
  }
  lines.push(
    '4) DETAILS OF CONCERN (required)',
    'Text only; please do not paste screenshots or images.',
    '',
    fields.details || '',
    '',
    '5) ACTIONS REQUIRED OF THE SPECIALTY / SERVICE (please select ONE only)',
    '',
  );
  for (const option of template.actions || []) {
    lines.push(`${tick(option.id === actionId)} ${option.label}`);
  }
  lines.push('', '6) HOW TO SUBMIT CONCERN(S) (please select ONE only)', '');
  for (const option of template.submissions || []) {
    lines.push(`${tick(option.id === submissionId)} ${option.label}`);
    if (option.guidance) lines.push(`   ${option.guidance}`);
    lines.push('');
  }
  lines.push(
    `Email completed form to ${template.email}`,
    'Send as a Word document or via Accumail (Accurx). Do NOT send as a PDF.',
    template.attachmentNote || '',
  );
  return lines.join('\n');
}

export function nclWordHtml(template, fields, context) {
  const providerId = selectedId(fields.provider);
  const actionId = selectedId(fields.action);
  const submissionId = selectedId(fields.submission);
  const concernIds = new Set(fields.concerns?.ids || []);
  const otherProvider = providerId === 'other' && fields.provider?.other
    ? `<p class="specify">Other (specify): <strong>${escapeHtml(fields.provider.other)}</strong></p>`
    : '';
  const actions = (template.actions || []).map((option) => (
    `<div class="choice${option.id === actionId ? ' picked' : ''}">${tick(option.id === actionId)} ${escapeHtml(option.label)}</div>`
  )).join('');
  const submissions = (template.submissions || []).map((option) => {
    const tone = option.id === 'feedback' ? 'tone-feedback' : option.id === 'patient-safety-event' ? 'tone-safety' : 'tone-alert';
    return `<div class="submit ${tone}${option.id === submissionId ? ' picked' : ''}"><div class="choice">${tick(option.id === submissionId)} ${escapeHtml(option.label)}</div><p>${escapeHtml(option.guidance || '')}</p></div>`;
  }).join('');

  return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word">
<head><meta charset="UTF-8"><title>${escapeHtml(template.displayName)}</title>
<style>
  body { font-family: Calibri, Arial, sans-serif; color: #1a1a1a; max-width: 820px; margin: 24px auto; font-size: 13px; }
  h1 { font-size: 20px; text-align: center; margin: 0 0 8px; }
  .lede { text-align: center; margin: 0 0 12px; line-height: 1.4; }
  .banner { background: #fff3bf; border: 2px solid #111; padding: 10px 12px; line-height: 1.45; margin: 0 0 14px; }
  table.identity { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
  table.identity td { border: 1px solid #111; padding: 5px 8px; }
  .required { color: #c40000; font-weight: 700; margin: 8px 0; }
  .section { border: 1px solid #111; padding: 10px 12px; margin: 0 0 12px; }
  h2 { font-size: 14px; margin: 0 0 6px; }
  .hint { margin: 0 0 8px; }
  table.layout { width: 100%; border-collapse: collapse; }
  table.layout td { vertical-align: top; padding: 0 14px 0 0; }
  h3 { font-size: 13px; margin: 8px 0 4px; }
  .choice { margin: 2px 0; }
  .choice.picked, .submit.picked .choice { font-weight: 700; background: #e7f6ec; }
  .field { border-bottom: 1px solid #111; min-height: 22px; margin: 0 0 10px; padding: 2px 0 4px; }
  .issue { border: 1px solid #111; min-height: 90px; padding: 8px; white-space: pre-wrap; }
  .tone-feedback .choice { color: #0b7a32; }
  .tone-safety .choice { color: #c40000; }
  .submit p { margin: 0 0 8px 22px; }
</style></head>
<body>
  <h1>${escapeHtml(template.displayName)}</h1>
  <p class="lede">This form is to be used for raising any concerns with a provider, by generating <strong>Feedback</strong>, a <strong>GP alert</strong>, or a <strong>Patient Safety Event</strong>.</p>
  <div class="banner">
    <div>Email completed form to: <strong>${escapeHtml(template.email)}</strong></div>
    <div>Send as a <strong>Word document</strong> or via Accumail / Accurx. Do NOT send the form as a PDF.</div>
    <p><strong>NEW / IMPORTANT:</strong> ${escapeHtml(template.attachmentNote || '')}</p>
  </div>
  <table class="identity"><tbody>
    <tr><td colspan="3">Date: ${escapeHtml(context?.completedOn || '')}</td></tr>
    <tr><td>Patient name: ${escapeHtml(context?.patientName || '')}</td><td>DOB: ${escapeHtml(context?.dateOfBirth || '')}</td><td>NHS number: ${escapeHtml(context?.nhsNumber || '')}</td></tr>
    <tr><td colspan="2">GP's name: ${escapeHtml(context?.raiserName || '')}</td><td>GP practice: ${escapeHtml(context?.practiceName || '')}</td></tr>
    <tr><td colspan="2">GP GMC number: ${escapeHtml(context?.gmcNumber || '')}</td><td>Practice code: ${escapeHtml(context?.odsCode || '')}</td></tr>
    <tr><td colspan="3">Reporter name (if not GP): ${escapeHtml(context?.reporterName || '')}</td></tr>
  </tbody></table>
  <p class="required">Please complete all sections marked *.<br>Sections marked “select ONE only” must not be left blank or have more than one option selected.</p>
  <div class="section">
    <h2>1) PROVIDER (please select ONE only)*</h2>
    <p class="hint">If your concern relates to more than one provider, please submit a separate form for each provider.</p>
    ${choiceHtmlSafe(template.providers, (id) => id === providerId)}
    ${otherProvider}
  </div>
  <div class="section">
    <h2>2) SPECIALTY / SERVICE (required — do not leave blank)*</h2>
    <div class="field">${escapeHtml(fields.specialty || '')}</div>
    <p>Specialty / service email (optional): ${escapeHtml(fields.specialtyEmail || '')}</p>
    <p>Consultant / lead clinician name (optional): ${escapeHtml(fields.consultantName || '')}</p>
  </div>
  <div class="section">
    <h2>3) NATURE OF CONCERN (select all that apply)*</h2>
    ${choiceHtmlSafe(template.concerns, (id) => concernIds.has(id))}
  </div>
  <div class="section">
    <h2>4) DETAILS OF CONCERN (required — do not leave blank)*</h2>
    <p class="hint">Please detail your concern professionally. Text only; please do not paste screenshots or images.</p>
    <div class="issue">${escapeHtml(fields.details || '')}</div>
  </div>
  <div class="section">
    <h2>5) ACTIONS REQUIRED OF THE SPECIALTY / SERVICE (please select ONE only)*</h2>
    ${actions}
  </div>
  <div class="section">
    <h2>6) HOW TO SUBMIT CONCERN(S) (please select ONE only)*</h2>
    ${submissions}
  </div>
  <div class="banner">
    <div>Email completed form to <strong>${escapeHtml(template.email)}</strong></div>
    <div>Send as a Word document or via Accumail (Accurx). Do NOT send as a PDF.</div>
    <div><strong>IMPORTANT:</strong> ${escapeHtml(template.attachmentNote || '')}</div>
  </div>
</body></html>`;

  function choiceHtmlSafe(groups, picked) {
    const blocks = (groups || []).map((group) => {
      const items = (group.options || []).map((option) => {
        const on = picked(option.id);
        return `<div class="choice${on ? ' picked' : ''}">${tick(on)} ${escapeHtml(option.label)}</div>`;
      }).join('');
      return `<h3>${escapeHtml(group.label)}</h3>${items}`;
    });
    const plan = columnPlan(blocks.length);
    const width = Math.floor(100 / plan.length);
    const cells = plan.map((indexes) => (
      `<td width="${width}%" valign="top">${indexes.map((index) => blocks[index]).join('')}</td>`
    )).join('');
    return `<table class="layout" width="100%" cellpadding="0" cellspacing="0"><tbody><tr>${cells}</tr></tbody></table>`;
  }

  function columnPlan(count) {
    if (count === 6) return [[0, 1], [2], [3, 4, 5]];
    if (count === 8) return [[0, 1, 2, 3, 4], [5, 6, 7]];
    const columns = count > 4 ? 3 : Math.min(count, 2);
    const size = Math.ceil(count / columns);
    return Array.from({ length: columns }, (_, column) => (
      Array.from({ length: size }, (__, row) => column * size + row).filter((index) => index < count)
    ));
  }
}
